import { useElements, useStripe, PaymentElement } from '@stripe/react-stripe-js';
import { useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { Box } from '@mui/material';
import { StripePaymentElementChangeEvent, PaymentIntent } from '@stripe/stripe-js';
import { useMutation } from 'react-query';
import { updatePaymentIntent } from '../../api/subscription.api';
import {
  getSelectedSubscription,
  SubscriptionPayment,
} from '../../../../core/store/slices/subscription.slice';
import { useCreatePaymentIntentMutation } from '../../hooks/subscriptionApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../core/store';
import { useSearchParams } from 'react-router-dom';
import { useTrialEligibilityQuery } from '../../hooks/useTrialEligibilityQuery';

export type PaymentIntentStatus = PaymentIntent['status'];

export interface PaymentResult {
  success: boolean;
  paymentIntent?: Partial<PaymentIntent>;
  errorType?: string;
  errorMessage?: string;
  subscriptionPayment?: SubscriptionPayment;
}

export interface PaymentInformationContentProps {
  onPaymentElementChange: (event: StripePaymentElementChangeEvent) => void;
}

export interface PaymentFormRef {
  submitPayment: () => Promise<PaymentResult>;
  resetPaymentElement: () => void;
  discount: number;
  setPromoCode?: (code: string) => void;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const PaymentInformationContent = forwardRef<PaymentFormRef, PaymentInformationContentProps>(
  ({ onPaymentElementChange }, ref) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isReady, setIsReady] = useState(false);
    const [promoCode, setPromoCode] = useState<string | null>(null);
    const profile = useSelector((state: RootState) => state.personalDetails);
    const authFlow = useSelector((state: RootState) => state.auth.authFlow);
    const selectedSubscription = useSelector(getSelectedSubscription);
    const createPaymentIntentMutation = useCreatePaymentIntentMutation();
    const { mutateAsync: verifyPaymentIntentMutation } = useMutation(
      ({ clientSecret, paymentId }: { clientSecret: string; paymentId?: string }) =>
        updatePaymentIntent(clientSecret, paymentId),
      {
        retry: false,
      }
    );
    const [searchParams, setSearchParams] = useSearchParams();
    const isAddOn = Boolean(searchParams.get('addOn'));
    const isNewEnrollmentParam = Boolean(searchParams.get('isNewEnrollment'));

    const basicInfo = useSelector((state: RootState) => state.basicInfo);
    const { data: trialEligibilityData } = useTrialEligibilityQuery(
      basicInfo._id,
      !isAddOn && !!basicInfo._id
    );
    const isTrialEligible = trialEligibilityData?.data?.trialEligible ?? false;

    useEffect(() => {
      if (authFlow === 'login' && isNewEnrollmentParam) {
        const params = new URLSearchParams(searchParams);
        params.delete('isNewEnrollment');
        setSearchParams(params, { replace: true });
      }
    }, [authFlow, isNewEnrollmentParam, searchParams, setSearchParams]);

    const [savedPaymentId, setSavedPaymentId] = useState<string | null>(null);
    useEffect(() => {
      setIsReady(!!stripe && !!elements);
    }, [stripe, elements]);
    const createSubscription = async () => {
      if (!stripe || !elements) {
        return {
          success: false,
          errorMessage: 'Payment processing is not ready yet',
          errorType: 'setup_error',
        };
      }
      try {
        const { error: submitError } = await elements.submit();
        if (submitError) {
          return {
            success: false,
            errorMessage: submitError.message || 'An unexpected error occurred',
            errorType: 'submit_error',
          };
        }

        let paymentMethod = null;
        if (!savedPaymentId) {
          paymentMethod = await stripe?.createPaymentMethod({
            element: elements?.getElement(PaymentElement)!,
            params: {
              billing_details: {
                name: profile.firstName + ' ' + profile.lastName,
                email: profile.email,
              },
            },
          });
        }

        const response = await createPaymentIntentMutation.mutateAsync({
          priceId: selectedSubscription?.priceId || '',
          paymentMethodId: savedPaymentId || paymentMethod?.paymentMethod?.id!,
          isRefCodeAvailable: false,
          isAddOn: isAddOn,
          promoCode: promoCode || undefined,
          referralDiscountPercent: 0,
          referralCode: undefined,
          trialPeriodDays: isTrialEligible && !isAddOn ? 7 : undefined,
        });

        if (response.data.paymentRequired === false || !response.data.clientSecret) {
          await delay(3000);
          const { data } = await verifyPaymentIntentMutation({
            clientSecret: '',
            paymentId: response.data.subscriptionId,
          });
          const subscriptionPayment = data.data;
          const finalStatus = subscriptionPayment?.paymentStatus || 'succeeded';
          const isSuccessful = finalStatus === 'succeeded' || finalStatus === 'processing';

          return {
            success: isSuccessful,
            paymentIntent: {
              status: finalStatus as PaymentIntentStatus,
            },
            errorType: !isSuccessful ? finalStatus : undefined,
            subscriptionPayment,
          };
        }

        const confirmResult = await stripe?.confirmCardPayment(response.data.clientSecret!);
        const { error, paymentIntent } = confirmResult || {};
        await delay(3000);
        const paymentId = isAddOn ? error?.payment_intent?.id || paymentIntent?.id : '';
        const { data } = await verifyPaymentIntentMutation({
          clientSecret: response.data.clientSecret,
          paymentId,
        });
        const subscriptionPayment = data.data;
        if (error) {
          return {
            success: false,
            errorMessage: error.message || 'Payment failed. Please try again.',
            errorType: error.type,
            subscriptionPayment,
          };
        }

        const finalStatus = subscriptionPayment?.paymentStatus || paymentIntent?.status;
        const isSuccessful = finalStatus === 'succeeded' || finalStatus === 'processing';

        return {
          success: isSuccessful,
          paymentIntent: paymentIntent
            ? {
                ...paymentIntent,
                status: finalStatus as PaymentIntentStatus,
              }
            : undefined,
          errorType: !isSuccessful ? finalStatus : undefined,
          subscriptionPayment,
        };
      } catch (error) {
        console.log(error);
      }
    };
    useImperativeHandle(ref, () => ({
      submitPayment: async (): Promise<PaymentResult> =>
        (await createSubscription()) as PaymentResult,

      resetPaymentElement: () => {
        if (elements) {
          const paymentElement = elements.getElement('payment');
          if (paymentElement) {
            paymentElement.clear();
          }
        }
      },
      discount: 0,
      setPromoCode: (code: string) => {
        setPromoCode(code);
      },
    }));

    const handlePaymentElementChange = (event: StripePaymentElementChangeEvent) => {
      if (event) {
        onPaymentElementChange(event);
        if (event?.value.payment_method?.id) {
          setSavedPaymentId(event?.value.payment_method?.id);
        }
      }
    };

    return (
      <Box>
        {isReady && (
          <PaymentElement
            id='payment-element'
            onChange={handlePaymentElementChange}
            options={{
              layout: {
                type: 'accordion',
                defaultCollapsed: false,
                radios: true,
                spacedAccordionItems: false,
              },
            }}
          />
        )}
      </Box>
    );
  }
);

export default PaymentInformationContent;
